// Cache Manager Utility
// Handles service worker cache clearing and management

export const cacheManager = {
  /**
   * Clear all service worker caches
   * Useful after login to ensure fresh data
   */
  clearAllCaches: async () => {
    console.log("🗑️ Clearing all service worker caches...");

    if (!("caches" in window)) {
      console.log("⚠️ Cache API not available");
      return false;
    }

    try {
      const cacheNames = await caches.keys();
      console.log(`  Found ${cacheNames.length} caches:`, cacheNames);

      await Promise.all(
        cacheNames.map((cacheName) => {
          console.log(`  Deleting cache: ${cacheName}`);
          return caches.delete(cacheName);
        }),
      );

      console.log("✅ All caches cleared successfully");
      return true;
    } catch (error) {
      console.error("❌ Error clearing caches:", error);
      return false;
    }
  },

  /**
   * Clear runtime caches only (keep static assets)
   */
  clearRuntimeCaches: async () => {
    console.log("🗑️ Clearing runtime caches...");

    if (!("caches" in window)) {
      console.log("⚠️ Cache API not available");
      return false;
    }

    try {
      const cacheNames = await caches.keys();
      const runtimeCaches = cacheNames.filter(
        (name) => name.includes("runtime") || name.includes("dynamic"),
      );

      console.log(
        `  Found ${runtimeCaches.length} runtime caches:`,
        runtimeCaches,
      );

      await Promise.all(
        runtimeCaches.map((cacheName) => {
          console.log(`  Deleting cache: ${cacheName}`);
          return caches.delete(cacheName);
        }),
      );

      console.log("✅ Runtime caches cleared successfully");
      return true;
    } catch (error) {
      console.error("❌ Error clearing runtime caches:", error);
      return false;
    }
  },

  /**
   * Send message to service worker to clear caches
   */
  notifyServiceWorkerToClearCache: async () => {
    if (!("serviceWorker" in navigator)) {
      console.log("⚠️ Service Worker not available");
      return false;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      if (registration.active) {
        registration.active.postMessage({ type: "CLEAR_CACHE" });
        console.log("✅ Sent CLEAR_CACHE message to service worker");
        return true;
      }
    } catch (error) {
      console.error("❌ Error notifying service worker:", error);
      return false;
    }
  },

  /**
   * Force service worker update
   */
  updateServiceWorker: async () => {
    if (!("serviceWorker" in navigator)) {
      console.log("⚠️ Service Worker not available");
      return false;
    }

    try {
      const registration = await navigator.serviceWorker.getRegistration();
      if (registration) {
        await registration.update();
        console.log("✅ Service worker update triggered");
        return true;
      }
    } catch (error) {
      console.error("❌ Error updating service worker:", error);
      return false;
    }
  },

  /**
   * Get cache statistics
   */
  getCacheStats: async () => {
    if (!("caches" in window)) {
      return { available: false };
    }

    try {
      const cacheNames = await caches.keys();
      const stats = {
        available: true,
        count: cacheNames.length,
        names: cacheNames,
        sizes: [],
      };

      // Get size of each cache (approximate)
      for (const cacheName of cacheNames) {
        const cache = await caches.open(cacheName);
        const keys = await cache.keys();
        stats.sizes.push({
          name: cacheName,
          itemCount: keys.length,
        });
      }

      return stats;
    } catch (error) {
      console.error("❌ Error getting cache stats:", error);
      return { available: true, error: error.message };
    }
  },
};

export default cacheManager;
